import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { availabilitiesApiSlice } from "../../../api/availabilities/availabilitiesApiSlice";
import CloseIcon from "../../../components/icons/CloseIcon";
import { useApplyCoupon } from "../../../redux/flows/applyCouponFlow";
import { useCreateNewReservation } from "../../../redux/flows/createReservationFlow";
import { useFetchGroups } from "../../../redux/flows/fetchGroupsFlow";
import { useRemoveReservation } from "../../../redux/flows/removeReservationFlow";
import {
  addAnotherGroup,
  bookingCreationCompleted,
  bookingFlowChanged,
  bookingStepChanged,
  dateSelectedClear,
  durationSelectedClear,
  navigatedToNextStep,
  setAvailabilities,
  setHasSelectedLocation,
  setIsAddingAnotherEquipment,
  setNextEnabled,
  setSelectedLocation,
  setUseCase,
  setAppliedCouponCode,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import { baseStore, RootState } from "../../../redux/store";
import routes from "../../../routes/routes";
import { usePrevious } from "../../../util/usePrevious";
import GeneralDialog from "../../common/GeneralDialog";
import Header from "../../layout/components/Header";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import TenantLogo from "../../layout/components/TenantLogo";
import BookLocationHeader from "../common/util/BookLocationHeader";
import BookLocationNavigateBackAction from "../common/util/BookLocationNavigateBackAction";
import BookLocationNavigateNextAction from "../common/util/BookLocationNavigateNextAction";
import ConfirmationStep from "./ConfirmationStep";
import EquipmentSelectionStep from "./EquipmentSelectionStep";
import UseCaseLocationSelectionStep from "../common/components/LocationSelectionStep/UseCaseLocationSelectionStep";
import { useNoBookableStation } from "../common/components/LocationSelectionStep/useNoBookableStation";
import OrderDataStep from "./OrderDataStep";
import PaymentStep from "./PaymentStep";
import PersonalDataStep from "./PersonalDataStep";
import { getSvcLocations } from "../../../util/utilFunctions";
import useServiceStorage from "../../../hooks/useServiceStorage";
import { ServiceTypeSelectorOption } from "../../../types/order";
import { useBookingWindow } from "../common/hooks/useBookingWindow";

export enum BookingCreationStep {
  locationSelection,
  assetGroupSelection,
  orderData,
  personalData,
  confirmation,
  paymentTypeSelection,
  payment,
}

const Root = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
  color: "white",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.default,
  paddingBottom: `calc(${theme.spacing(1)}px + env(safe-area-inset-bottom, 0))`,
  display: "flex",
  flexDirection: "column",
}));

const StepWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  overflowY: "auto",
  paddingTop: theme.spacing(3),
  [theme.breakpoints.up("lg")]: {
    paddingTop: theme.spacing(6),
  },
}));

const ActionsWrapper = styled(Box)(({ theme }) => ({
  width: "calc(100% - 42px)",
  maxWidth: 1000,
  margin: "0 auto",
  paddingBottom: theme.spacing(5),
  // position: 'fixed',
  bottom: 10,
  left: 0,
  right: 0,
  [theme.breakpoints.up("lg")]: {
    paddingBottom: theme.spacing(5),
  },
}));

export const LogoBox = styled(Box)(({ theme }) => ({
  width: "150px",
  marginTop: theme.spacing(1),
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
}));

interface BookLocationDialogProps {
  returnPage: string;
}

const BookLocationPage = (props: BookLocationDialogProps) => {
  const stepWrapperRef = useRef<HTMLElement>();
  const { updateService } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.RENTAL
  );

  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  // The configured booking horizon for this use case — replaces the hardcoded
  // `add(1, "months").endOf("month")` this file computed inline. One month is
  // the DEFAULT here, so an unconfigured tenant requests exactly the same range
  // as before. `earliest` carries the lead buffer, which defaults to 0 for this
  // use case (the customer may book now), and is the same rule the app API
  // enforces (ADR-0020, `backend/src/core/lib/bookingWindow.ts`).
  const bookingWindow = useBookingWindow("rental");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // const locationId = props.locationId;
  const step = useSelector((state: RootState) => state.bookingCreation.step);
  const previousStep = usePrevious(step);
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  const reservations = useSelector(
    (state: RootState) => state.bookingCreation.reservations
  );
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  // const { data: equipements } = useGetLocationEquipmentQuery({ locationId: selectedLocation?.id });
  const assetGroupId = useSelector(
    (state: RootState) => state.bookingCreation.assetGroupId
  );

  const appliedCouponCode = useSelector(
    (state: RootState) => state.bookingCreation.appliedCouponCode
  );
  const locations = useSelector((state: RootState) => state.booking.locations);

  const createNewReservation = useCreateNewReservation();
  const applyCoupon = useApplyCoupon();
  const removeReservation = useRemoveReservation();
  const fetchGroups = useFetchGroups("rental");

  const paymentProvider = useSelector(
    (state: RootState) => state.bookingCreation.payment.provider
  );

  // Handle voucher code from URL parameters
  useEffect(() => {
    const voucherCode = searchParams.get('voucher');
    if (voucherCode && voucherCode.trim() && !appliedCouponCode) {
      // Store the voucher code from banner for later application during confirmation step
      dispatch(setAppliedCouponCode(voucherCode.trim()));
    }
  }, [searchParams, appliedCouponCode, dispatch]);

  useEffect(() => {
    if (reservations.length === 0) {
      // dispatch(loadBookingFlow({}));
      stepWrapperRef.current = document.getElementById(
        "bookingProcessStepWrapper"
      )!;
      // @todo body scroll lock breaks the phone number input. Maybe remove it entirely?
      //disableBodyScroll(stepWrapperRef.current);
      //return () => enableBodyScroll(stepWrapperRef.current!);
      dispatch(
        bookingStepChanged({
          step: BookingCreationStep.locationSelection,
          nextEnabled: !!selectedLocation,
        })
      );

      // Set the usecase
      dispatch(setUseCase({ useCase: "rental" }));

      // Set the bookingFlow
      dispatch(
        bookingFlowChanged({
          bookingFlow: [
            "locationSelection",
            "assetGroupSelection",
            "orderData",
            "confirmation",
            "personalData",
            "paymentTypeSelection",
            "payment",
          ],
        })
      );
    }
  }, []);

  const fetchAssetGroupAvailabilities = async (
    assetGroupId: string,
    from: Date,
    to: Date
  ) => {
    const data = await baseStore.dispatch(
      availabilitiesApiSlice.endpoints.fetchAssetGroupAvailabilities.initiate({
        assetGroupId: assetGroupId,
        from: from,
        to: to,
      })
    );
    return data;
  };

  useEffect(() => {
    if (
      reservations.length === 0 &&
      step === BookingCreationStep.confirmation
    ) {
      dispatch(bookingCreationCompleted());
    }
  }, [reservations]);

  useEffect(() => {
    const manageReduxData = async () => {
      if (step === BookingCreationStep.assetGroupSelection) {
        // Clean data
        dispatch(dateSelectedClear());
        dispatch(durationSelectedClear());
        await fetchGroups();
      } else if (
        step === BookingCreationStep.orderData &&
        assetGroupId &&
        previousStep &&
        !(previousStep > BookingCreationStep.orderData)
      ) {
        // TODO change to user input
        const fromDate = bookingWindow.earliest.toDate();
        const toDate = bookingWindow.latest.toDate();
        const { data: availabilities } = await fetchAssetGroupAvailabilities(
          assetGroupId,
          fromDate,
          toDate
        );

        if (availabilities) {
          dispatch(setAvailabilities(availabilities.availabilities));
        }
      }
      // // previous step is orderData, current step is personal data
      if (
        previousStep === BookingCreationStep.personalData &&
        step === BookingCreationStep.confirmation
      ) {
        const processedReservation = await createNewReservation();

        updateService(ServiceTypeSelectorOption.RENTAL);

        if (processedReservation && appliedCouponCode) {
          // Due to asynchronous nature of redux; we add the returned processed reservation to previous state
          const reservationIds = reservations.map(
            (reservation: any) => reservation.reservationId!
          );
          reservationIds.push(processedReservation.reservationId!);
          await applyCoupon(reservationIds, appliedCouponCode);
        }
      }

      // previous step is confirmation, current step is personal data
      // user navigated back from booking summary, so delete reservation
      if (
        previousStep === BookingCreationStep.confirmation &&
        step === BookingCreationStep.personalData
      ) {
        await removeReservation();
      }

      // // previous step is personal data, current step is board selection
      // // or previous step is personal data, current step is orderData
      if (
        step === BookingCreationStep.orderData &&
        assetGroupId &&
        previousStep &&
        previousStep > BookingCreationStep.orderData
      ) {
        // user navigated back, so delete reservation
        await removeReservation();
        const fromDate = bookingWindow.earliest.toDate();
        const toDate = bookingWindow.latest.toDate();
        const { data: availabilities } = await fetchAssetGroupAvailabilities(
          assetGroupId,
          fromDate,
          toDate
        );
        if (availabilities) {
          dispatch(setAvailabilities(availabilities.availabilities));
        }
      }

      if (
        previousStep === BookingCreationStep.personalData &&
        step === BookingCreationStep.confirmation &&
        paymentProvider &&
        paymentProvider.length > 1
      ) {
        // when there is more than one payment provider configured, we need to initialize payment type selection
        // dispatch(initializePaymentTypeSelection({}));
      }
      // previous step is payment summary, current step is payment
      // or previous step is payment type selection, current step is payment
      if (
        (previousStep === BookingCreationStep.confirmation &&
          step === BookingCreationStep.payment) ||
        (previousStep === BookingCreationStep.paymentTypeSelection &&
          step === BookingCreationStep.payment)
      ) {
        // dispatch(initializePaymentProvider({}));
      }

    };
    manageReduxData();
  }, [step]);

  // Every station closed (e.g. winter hibernation): the location step shows a
  // screen of its own, so the step title and "Weiter" step aside.
  const noBookableStation = useNoBookableStation("rental");
  const showClosedScreen =
    step === BookingCreationStep.locationSelection && noBookableStation;

  const headerText = useMemo(() => {
    switch (step) {
      case BookingCreationStep.locationSelection:
        return t("book_location.select_location");
      case BookingCreationStep.assetGroupSelection:
        return t("book_location.select_equipment");
      case BookingCreationStep.orderData:
        return t("book_location.order_data.header_text");
      case BookingCreationStep.personalData:
        return t("book_location.personal_data.header_text");
      case BookingCreationStep.confirmation:
        return t("book_location.confirmation.header_text");

      default:
        return "";
    }
  }, [step, orderData.date]);

  const isLoading = () => {
    if (
      step === BookingCreationStep.personalData ||
      step === BookingCreationStep.confirmation
    ) {
      return false;
    }

    return false;
  };

  const closePage = () => {
    navigate(routes.home);
    dispatch(bookingCreationCompleted());
  };

  const handleAddAnotherEquipment = () => {
    dispatch(setIsAddingAnotherEquipment(true));
    dispatch(addAnotherGroup({}));
  };

  const isLastStep = step === BookingCreationStep.payment;

  useEffect(() => {
    if (locations && step === 0) {
      const svcLocations = getSvcLocations(locations, "rental");
      if (svcLocations.length === 1) {
        dispatch(navigatedToNextStep());
        dispatch(setSelectedLocation(svcLocations[0]));
        dispatch(setHasSelectedLocation(true));
        // setHasSelectedLocation unconditionally enables "Next" for non-room
        // use cases, which undoes the reset navigatedToNextStep just did. On a
        // single-location tenant that lands the user on the equipment step with
        // "Next" live and no equipment selected — advancing then hangs on
        // availabilities for a null group. Same guard the SUP flow already has.
        dispatch(setNextEnabled(false));
      }
    }
  }, [locations, step]);

  // When only one rental location exists, locationSelection is auto-skipped
  // (see effect above), so assetGroupSelection becomes the effective first
  // step with no usable previous step. Hide the back button in that case to
  // avoid a non-functional control that would just re-trigger the auto-skip.
  const hasSingleRentalLocation = useMemo(() => {
    if (!locations) return false;
    return getSvcLocations(locations, "rental").length === 1;
  }, [locations]);
  const hideBackButton =
    step === BookingCreationStep.assetGroupSelection && hasSingleRentalLocation;

  return (
    <Root>
      <Header>
        <div className="h-12 absolute left-2 flex items-center">
          {!hideBackButton && <BookLocationNavigateBackAction />}
        </div>
        <TenantLogo />
        <button
          id="closeButton"
          className="size-12 absolute right-0 text-foreground/90"
          onClick={() => setPageDialogOpen(true)}
        >
          <CloseIcon />
        </button>
      </Header>
      <Box sx={{ marginTop: "20px" }}>
        {!showClosedScreen && <BookLocationHeader text={headerText} />}
      </Box>
      <StepWrapper id={"bookingProcessStepWrapper"}>
        {step === BookingCreationStep.locationSelection && (
          <UseCaseLocationSelectionStep service="rental" />
        )}
        {step === BookingCreationStep.assetGroupSelection && (
          <EquipmentSelectionStep />
        )}
        {step === BookingCreationStep.orderData && <OrderDataStep />}
        {step === BookingCreationStep.confirmation && (
          <ConfirmationStep onAddAnotherEquipment={handleAddAnotherEquipment} />
        )}
        {step === BookingCreationStep.personalData && <PersonalDataStep />}
        {step === BookingCreationStep.payment && <PaymentStep />}
      </StepWrapper>
      <ActionsWrapper>
        {!isLastStep && !showClosedScreen && <BookLocationNavigateNextAction />}
      </ActionsWrapper>
      <LoaderOverlay open={isLoading()} />
      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </Root>
  );
};

export default BookLocationPage;
